import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { StorageCheckoutStepProps } from '@root/components/checkout/types';

import {
  hasChallengerPickupAddress,
  normalizeCheckoutZip,
} from '../completion';
import {
  Card,
  HelpText,
  Question,
  SectionStack,
  SectionTitle,
} from '../shared';
import { CHALLENGER as T } from '../tokens';
import { ChallengerAddressFields } from './address_fields';
import { ChallengerAppointmentCalendar } from './appointment_calendar';

const AddressCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
`;

/** Figma 1:3247 — question + schedule description, 4px gap. */
const AddressHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[4]};
`;

type Props = {
  stepProps: StorageCheckoutStepProps;
};

/**
 * Figma Desktop 03 / mobile pickup block:
 * 1) Address card (street + apt)
 * 2) Calendar card (“When should we arrive?”)
 * 3) Arrival card (“What time works best for you?”) — always visible
 *
 * When pickup ZIP changes, clear the address and selected appointment so stale
 * values are not shown after Storage location is updated.
 */
export const PickupSection: React.FC<Props> = ({ stepProps }) => {
  const {
    onChange,
    values: { address, zip },
  } = stepProps;

  const previousZipRef = useRef<string | undefined>(undefined);
  const previousAddressReadyRef = useRef<boolean | undefined>(undefined);
  const pendingAddressZipSyncRef = useRef<string | undefined>(undefined);
  const [storageZipRevision, setStorageZipRevision] = useState(0);
  const addressReady = hasChallengerPickupAddress(address);

  const handleAddressZipSync = (normalizedZip: string) => {
    pendingAddressZipSyncRef.current = normalizedZip;
  };

  useEffect(() => {
    const previousZip = previousZipRef.current;

    if (!zip) {
      if (previousZip !== undefined && previousZip !== '') {
        onChange('address', {});
        onChange('datePreferred', undefined);
        onChange('dateScheduled', undefined);
        setStorageZipRevision((revision) => revision + 1);
      }
      previousZipRef.current = undefined;
      pendingAddressZipSyncRef.current = undefined;
      return;
    }

    const normalizedZip = normalizeCheckoutZip(zip);

    if (previousZip !== undefined && previousZip !== normalizedZip) {
      const pendingSync = pendingAddressZipSyncRef.current;
      if (pendingSync === normalizedZip) {
        pendingAddressZipSyncRef.current = undefined;
        previousZipRef.current = normalizedZip;
        return;
      }

      const addressZip = normalizeCheckoutZip(address?.zip);
      // Address autocomplete also updates checkout ZIP — do not wipe that pick.
      if (hasChallengerPickupAddress(address) && addressZip === normalizedZip) {
        previousZipRef.current = normalizedZip;
        return;
      }

      onChange('address', {});
      onChange('datePreferred', undefined);
      onChange('dateScheduled', undefined);
      setStorageZipRevision((revision) => revision + 1);
    }
    previousZipRef.current = normalizedZip;
  }, [address, onChange, zip]);

  useEffect(() => {
    const previousReady = previousAddressReadyRef.current;
    if (previousReady === true && !addressReady) {
      onChange('datePreferred', undefined);
      onChange('dateScheduled', undefined);
    }
    previousAddressReadyRef.current = addressReady;
  }, [addressReady, onChange]);

  return (
    <SectionStack>
      <SectionTitle>Pickup details</SectionTitle>

      <AddressCard>
        <AddressHeader>
          <Question>
            Enter the address where we will pick up your items.
          </Question>
          {!addressReady && (
            <HelpText>
              Enter a complete address (including city and ZIP) to see pickup
              availability.
            </HelpText>
          )}
        </AddressHeader>
        <ChallengerAddressFields
          {...stepProps}
          onAddressZipSync={handleAddressZipSync}
          storageZipRevision={storageZipRevision}
        />
      </AddressCard>

      <ChallengerAppointmentCalendar
        onChange={stepProps.onChange}
        enabled={stepProps.enabled}
        values={stepProps.values}
      />
    </SectionStack>
  );
};
